import React from "react";
import {
  Card,
  CardContent,
  Typography,
  useTheme,
  Stack,
  CircularProgress,
} from "@mui/material";
import CardHeader from "./CardHeader.jsx";
import { faCalendarLinesPen } from "@fortawesome/pro-solid-svg-icons";
import * as f from "../../../utils/formatter.js";
import { useLatest } from "../../../hooks.js";
import * as api from "../../../services/api.js";
import { isGrantedAny, role } from "shared/src/roles.mjs";
import { useRoles } from "../../../context/AuthenticationTokenContext";

function useEmployeeNameData(ids) {
  const [state, setState] = React.useState({
    loading: true,
    data: [],
  });

  useLatest(
    async (signal) => {
      try {
        setState((state) => ({ ...state, loading: true }));
        const { data } = await api.loadEmployeeNameData(ids);
        setState((state) => ({ ...state, data: data.employees.data }));
      } finally {
        if (!signal.aborted) {
          setState((state) => ({ ...state, loading: false }));
        }
      }
    },
    [ids],
  );

  return state;
}

function crerateArray(...items) {
  return items.filter((item) => item != null);
}

function LastNurse({ employeeAssignmentAudit }) {
  const ids = React.useMemo(
    () => crerateArray(employeeAssignmentAudit?.changed_fields?.employee_id),
    [employeeAssignmentAudit],
  );
  const employees = useEmployeeNameData(ids);

  if (employees.loading) {
    return <CircularProgress size={13} />;
  }

  return f.value(employees.data.map((e) => f.fullName(e)).join(", "));
}

function Info({ label, value }) {
  return (
    <Typography variant="body1">
      <strong>{label}</strong>
      <br />
      {value}
    </Typography>
  );
}

export function MoreInfoCard({
  client,
  lastAppointment,
  displayMinimalInfo = false,
}) {
  const theme = useTheme();

  const roles = useRoles();
  const canSeeClientClinic = isGrantedAny(
    roles,
    new Set([
      role.Floor_Manager,
      role.Back_Office,
      role.Front_Office,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only,
      role.Read_Only_Full,
    ]),
  );
  const canSeeClientCoordinator = isGrantedAny(
    roles,
    new Set([
      role.Floor_Manager,
      role.Treatment_Coordinator,
      role.Back_Office,
      role.Front_Office,
      role.Dental_Assistant,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only,
      role.Read_Only_Full,
    ]),
  );
  const canSeeLastNurse = isGrantedAny(
    roles,
    new Set([
      role.Floor_Manager,
      role.Back_Office,
      role.Front_Office,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only_Full,
    ]),
  );
  return (
    <Card>
      <CardContent>
        <CardHeader
          title="Další info"
          color={theme.palette.info.main}
          faIcon={faCalendarLinesPen}
        />
        <Stack spacing={5} direction="row" sx={{ mb: 3 }}>
          {!displayMinimalInfo && (
            <>
              <Info
                label="Jazyk klienta"
                value={f.value(client?.prefs?.language)}
              />
              <Info
                label="Další jazyky"
                value={f.value(
                  client?.languageInfoTags?.map((t) => t.value).join(", "),
                )}
              />
            </>
          )}
          {canSeeClientClinic && (
            <Info label="Klinika" value={f.value(client?.location?.name)} />
          )}
        </Stack>
        <Stack spacing={5} direction="row" sx={{ mb: 3 }}>
          <>
            {canSeeClientCoordinator && (
              <Info
                label="Koordinátor"
                value={f.value(f.fullName(client?.coordinator))}
              />
            )}
            <Info
              label="Poslední app."
              value={
                lastAppointment == null
                  ? f.value(null)
                  : `${lastAppointment.service?.name}, ${f.date(lastAppointment.start)}`
              }
            />
            {canSeeLastNurse && (
              <Info
                label="Poslední ošetřující sestra"
                value={
                  <LastNurse
                    employeeAssignmentAudit={
                      lastAppointment?.employeeAssignmentAudits?.data?.[0]
                    }
                  />
                }
              />
            )}
          </>
        </Stack>
      </CardContent>
    </Card>
  );
}
